import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { reduxForm, Field, change } from "redux-form";

import { Button, ProgressIndicator, ReduxControls, ErrorBanner } from "rhombus-react";

import { closeModal } from "reducers/Modal";
import { getImposiumProjects, switchCreativeComposition, clearImposiumErrors } from "reducers/ImposiumProjects";
import { getImposiumCompositions, clearCompositions } from "reducers/ImposiumCompositions";
import { loadCreatives } from "reducers/Creatives";

import { noop } from "utils/common";
import { SWITCH_IMPOSIUM_COMPOSITION_FORM } from "widgets/forms/list";
import BasicModal from "widgets/modals/BasicModal";
import { requiredNotNull } from "widgets/forms/validators/validators";

import "./SwitchCompositionModal.css";


export function SwitchCompositionModal(props){
	const dispatch = useDispatch();
	const { creativeId, creativeName, libraryId, currentProjectId, handleSubmit, invalid } = props;

	const {
		projects,
		isLoadingProjects,
		isSaving,
		errorObj,
		compositions,
		isLoadingCompositions,
	} = useSelector((state) => {
		return {
			projects: state.imposiumProjects.items,
			isLoadingProjects: state.imposiumProjects.isLoading,
			isSaving: state.imposiumProjects.isSaving,
			errorObj: state.imposiumProjects.error || {},
			compositions: state.imposiumCompositions.items,
			isLoadingCompositions: state.imposiumCompositions.isLoading,
		};
	});

	const isLoading = isLoadingProjects || isLoadingCompositions || isSaving;

	// Fetch projects on mount and clear any previous errors
	useEffect(() => {
		dispatch(clearImposiumErrors());
		dispatch(getImposiumProjects(libraryId));
		return () => {
			dispatch(clearCompositions());
		};
	}, [libraryId]);

	// Once projects are loaded, auto-select the current project and load its compositions
	useEffect(() => {
		if (projects.length > 0 && currentProjectId) {
			const currentProject = projects.find(proj => proj.id === currentProjectId);
			if (currentProject) {
				// Set the project dropdown to the current project
				dispatch(change(SWITCH_IMPOSIUM_COMPOSITION_FORM, 'project', {
					value: currentProject.id,
					label: currentProject.name,
				}));
				// Fetch compositions for the current project
				dispatch(getImposiumCompositions(libraryId, currentProject.id, currentProject.name));
			}
		}
	}, [projects, currentProjectId, libraryId]);

	// Build project options for dropdown
	const projectOptions = useMemo(() => {
		return projects.map(project => ({
			value: project.id,
			label: project.name,
		}));
	}, [projects]);

	// Build composition options for dropdown
	const compositionOptions = useMemo(() => {
		return compositions.map(comp => ({
			value: comp.id,
			label: comp.name,
		}));
	}, [compositions]);

	// Handle project selection change
	function handleProjectChange(selectedOption) {
		if (selectedOption?.value) {
			const project = projects.find(proj => proj.id === selectedOption.value);
			dispatch(getImposiumCompositions(libraryId, selectedOption.value, project?.name));
			// Clear the composition selection when project changes
			dispatch(change(SWITCH_IMPOSIUM_COMPOSITION_FORM, 'composition', null));
		} else {
			dispatch(clearCompositions());
			dispatch(change(SWITCH_IMPOSIUM_COMPOSITION_FORM, 'composition', null));
		}
	}

	function onClose(){
		dispatch(clearCompositions());
		dispatch(clearImposiumErrors());
		dispatch(closeModal());
	}

	const onSubmit = handleSubmit((values) => {
		const projectId = values.project?.value;
		const compositionId = values.composition?.value;
		dispatch(switchCreativeComposition(libraryId, creativeId, projectId, compositionId))
			.then((resp) => {
				if (resp.success) {
					dispatch(loadCreatives(libraryId));
					onClose();
				}
			});
	});

	function renderContent(){
		return (
			<div className="switch-composition-container">
				<ProgressIndicator isLoading={isLoading} />
				{!!Object.keys(errorObj)?.length && (
					<ErrorBanner
						title="Sorry, please fix the errors below and try again."
						messages={[errorObj.description]}
					/>
				)}
				<form id={SWITCH_IMPOSIUM_COMPOSITION_FORM} className="switch-composition-form" onSubmit={onSubmit}>
					<Field
						name="project"
						component={ReduxControls.Select}
						label="Imposium Project"
						placeholder="Select a project..."
						options={projectOptions}
						isClearable={false}
						isLoading={isLoadingProjects}
						onChange={handleProjectChange}
						onMenuScrollToBottom={noop}
						required
						validate={[requiredNotNull]}
					/>
					<Field
						name="composition"
						component={ReduxControls.Select}
						label="Composition"
						placeholder={compositions.length ? "Select a composition..." : "Select a project first"}
						options={compositionOptions}
						isClearable={false}
						isLoading={isLoadingCompositions}
						isDisabled={!compositions.length}
						onMenuScrollToBottom={noop}
						required
						validate={[requiredNotNull]}
					/>
				</form>
			</div>
		);
	}

	function renderFooter(){
		return ([
			<Button key="cancel" theme="default" onClick={onClose} disabled={isSaving}>Cancel</Button>,
			<Button
				key="submit"
				theme="green"
				disabled={invalid || isLoading}
				type="submit"
				form={SWITCH_IMPOSIUM_COMPOSITION_FORM}
			>
				Update Composition
			</Button>
		]);
	}

	const modalProps = {
		title: `Switch Project Composition - ${creativeName}`,
		className: "switch-composition-modal",
		onClose,
		renderContent,
		renderFooter,
	};

	return <BasicModal {...modalProps} />;
}

SwitchCompositionModal.displayName = "SwitchCompositionModal";

export default reduxForm({
	form: SWITCH_IMPOSIUM_COMPOSITION_FORM,
})(SwitchCompositionModal);
